import { useEffect, useRef } from "react";
import { apiClient } from "./api";
import { useAuth } from "./auth";

/**
 * useAuctionAlerts
 * ────────────────
 * Polls the user's active auctions (auctions they've bid on which are
 * still live) every 60 seconds. When any of them enter the "final 10
 * minutes" window OR the user is outbid, we fire a local browser
 * Notification (via the standard Notification API).
 *
 * This works without a full Web-Push server (no VAPID keys needed).
 * The notification is delivered as long as the tab / PWA is open in the
 * background — which covers the majority of "sniping" scenarios where
 * bidders alt-tab away right before the end.
 *
 * We de-dupe alerts per vehicle so we don't spam the user; each auction
 * fires "ending-soon" at most once and "outbid" whenever the leader
 * changes to somebody other than the current user.
 */
export default function useAuctionAlerts() {
  const { user } = useAuth();
  const notifiedEndingRef = useRef(new Set());
  const lastLeaderRef = useRef(new Map()); // vehicleId -> last leader user_id
  const permissionAskedRef = useRef(false);

  useEffect(() => {
    if (!user) return undefined;
    if (typeof window === "undefined") return undefined;
    if (!("Notification" in window)) return undefined;

    // Ask permission once, silently
    if (!permissionAskedRef.current && Notification.permission === "default") {
      permissionAskedRef.current = true;
      Notification.requestPermission().catch(() => {});
    }

    const fire = (title, body, tag) => {
      if (Notification.permission !== "granted") return;
      try {
        const n = new Notification(title, {
          body,
          tag,
          icon: "/logo192.png",
          badge: "/logo192.png",
        });
        n.onclick = () => {
          window.focus();
          if (tag?.startsWith("cartal-auction:")) {
            const vid = tag.split(":")[1];
            window.location.href = `/vehicle/${vid}`;
          }
        };
      } catch {
        /* ignore */
      }
    };

    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await apiClient.get("/auctions/my-active");
        const items = data.items || [];
        const now = Date.now();
        items.forEach((v) => {
          const ends = v.auction_ends_at ? new Date(v.auction_ends_at).getTime() : 0;
          const msLeft = ends - now;
          const label = `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim();

          // Ending-soon alert (≤ 10 mins, > 0)
          if (msLeft > 0 && msLeft <= 10 * 60 * 1000 && !notifiedEndingRef.current.has(v.id)) {
            notifiedEndingRef.current.add(v.id);
            const mins = Math.max(1, Math.round(msLeft / 60000));
            fire(
              "ينتهي مزادك قريباً!",
              `${label} — يتبقى ${mins} دقيقة. لا تفوّت فرصتك!`,
              `cartal-auction:${v.id}:ending`,
            );
          }

          // Outbid alert — leader changed and it's no longer this user
          const prev = lastLeaderRef.current.get(v.id);
          const leader = v.current_bid_user_id;
          if (prev !== undefined && leader && leader !== user.id && prev !== leader) {
            fire(
              "تمّت مزايدتك!",
              `${label} — قدّم عرضاً أعلى الآن قبل انتهاء المزاد.`,
              `cartal-auction:${v.id}:outbid`,
            );
          }
          if (leader) lastLeaderRef.current.set(v.id, leader);
        });
      } catch {
        /* silently ignore — user may be logged out */
      }
    };

    check();
    const tm = setInterval(() => {
      if (!cancelled) check();
    }, 60000); // every 60s
    return () => {
      cancelled = true;
      clearInterval(tm);
    };
  }, [user]);
}
